import {
  ActionIcon,
  Badge,
  Group,
  rem,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { useProfileStore } from "@pages/ClientProfile/store/profileStore";
import { useDiagnosticStore } from "./store";
import { IconChevronDown, IconChevronUp, IconX } from "@tabler/icons-react";
import { delayMS, findDocumentById, navigateToField } from "../../utils";
import type { ClientFieldError } from "../../types";
import { startTransition, useEffect } from "react";
import { showNotification } from "@mantine/notifications";

interface ErrorFieldTrackerProps {
  onClose: () => void;
}

const ErrorFieldTracker = ({ onClose }: ErrorFieldTrackerProps) => {
  const { sidebarCollapsed, setSelectedDoc, setIsSplitView, diagnosticErrors } =
    useProfileStore();
  const { selectedDiagnosticField, setSelectedDiagnosticField } =
    useDiagnosticStore();

  const isMobileOrTablet = useMediaQuery("(max-width: 980px)");
  const fieldErrors = Object.values(diagnosticErrors).flat();
  const index = fieldErrors.findIndex(
    (error) => error.key === selectedDiagnosticField
  );

  const navigateToError = async (fieldError: ClientFieldError) => {
    setSelectedDiagnosticField(fieldError.key);

    // Check if we need to navigate to a different page
    if (fieldError.document) {
      const document = findDocumentById(String(fieldError.document.formId));
      if (!document) {
        showNotification({
          color: "red",
          message: `Could not find document (${fieldError.document.formId}) associated with diagnostic error :(`,
        });
        return;
      }

      startTransition(() => {
        setSelectedDoc(document);
        setIsSplitView(true);
      });
    } else {
      startTransition(() => {
        setSelectedDoc(null);
        setIsSplitView(false);
      });
    }

    // Navigate to the field
    requestAnimationFrame(async () => {
      await delayMS(200);
      await navigateToField(fieldError.key);
    });
  };

  // Auto-navigate to next error when current error is resolved
  useEffect(() => {
    if (selectedDiagnosticField && index === -1 && fieldErrors.length > 0) {
      const timeoutId = setTimeout(() => {
        navigateToError(fieldErrors[0]);
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [diagnosticErrors, selectedDiagnosticField, index, fieldErrors.length]);

  // EARLY RETURN MOVED HERE - after all hooks
  if (fieldErrors.length === 0 || index === -1) {
    return null;
  }

  const currentError = fieldErrors[index];
  const isFirstError = index === 0;
  const isLastError = index === fieldErrors.length - 1;
  const shouldCenter = isMobileOrTablet || sidebarCollapsed;

  const containerStyles = {
    zIndex: 10000,
    position: "fixed" as const,
    bottom: "3.5rem",
    left: shouldCenter
      ? "50%"
      : "calc(350px + (100vw - 350px - 34.275rem) / 2)",
    transform: shouldCenter ? "translateX(-50%)" : "none",
    width: "34.275rem",
    borderRadius: "0.5rem",
  };

  const disabledStyles = (isDisabled: boolean) => ({
    root: {
      opacity: isDisabled ? 0.3 : 1,
      cursor: isDisabled ? "not-allowed" : "pointer",
      transition: "opacity 0.2s ease",
      "&:hover": {
        opacity: isDisabled ? 0.3 : 0.8,
        backgroundColor: isDisabled
          ? "transparent"
          : "rgba(255, 255, 255, 0.1)",
      },
    },
  });

  return (
    <Group
      style={containerStyles}
      justify="space-between"
      gap="0.5rem"
      bg="rgba(30, 30, 30, 0.8)"
      p="md"
      className="error-field-tracker"
    >
      <Stack gap="0.5rem">
        <Group>
          <Badge size="lg" color="red" variant="default">
            <Text fw={700}>
              Issue {index + 1} of {fieldErrors.length}
            </Text>
          </Badge>

          <Badge
            style={{
              backgroundColor: currentError?.document ? "#ACD3F0" : "#F5F5F5",
            }}
            size="lg"
            radius="0.25rem"
            variant="light"
          >
            <Text tt="uppercase" size="md" c="#303030" fw={600}>
              {currentError?.document?.formType ?? "Main Info"}
            </Text>
          </Badge>
        </Group>
        <Title fw={500} c="#fff" order={3}>
          {currentError.label.replace("Missing Required Field: ", "")}
        </Title>
        <Text style={{ fontSize: rem(16), fontStyle: "italic", color: "#fff" }}>
          If there is no issue, click and unclick textbox
        </Text>
      </Stack>

      <Group>
        <ActionIcon
          disabled={isFirstError}
          onClick={() =>
            !isFirstError && navigateToError(fieldErrors[index - 1])
          }
          variant="transparent"
          styles={disabledStyles(isFirstError)}
        >
          <IconChevronUp color={isFirstError ? "#666" : "#fff"} size={24} />
        </ActionIcon>

        <ActionIcon
          disabled={isLastError}
          onClick={() =>
            !isLastError && navigateToError(fieldErrors[index + 1])
          }
          variant="transparent"
          styles={disabledStyles(isLastError)}
        >
          <IconChevronDown color={isLastError ? "#666" : "#fff"} size={24} />
        </ActionIcon>

        <ActionIcon
          onClick={onClose}
          variant="transparent"
          style={{ cursor: "pointer" }}
        >
          <IconX color="#fff" size={24} />
        </ActionIcon>
      </Group>
    </Group>
  );
};

export default ErrorFieldTracker;
